<template>
  <Teleport to="body">
    <Transition name="confirm-modal">
      <div
        v-if="open"
        class="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-on-background/50 backdrop-blur-sm"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        :aria-describedby="messageId"
        @click.self="handleCancel"
      >
        <div
          ref="dialogRef"
          class="bg-surface rounded-xl border border-outline-variant/30 shadow-xl w-full max-w-md overflow-hidden"
          @keydown="trapFocus"
        >
          <div class="px-6 pt-6 pb-4">
            <h2
              :id="titleId"
              class="text-lg font-semibold text-on-surface"
            >
              {{ title }}
            </h2>
            <p
              :id="messageId"
              class="mt-2 text-sm text-on-surface-variant"
            >
              {{ message }}
            </p>
          </div>
          <div class="px-6 pb-6 flex items-center justify-end gap-3">
            <button
              ref="cancelBtnRef"
              type="button"
              class="px-4 py-2 text-sm font-medium rounded-full border border-outline-variant/40 text-on-surface-variant hover:bg-surface-container-low transition-colors"
              :aria-label="cancelLabel"
              @click="handleCancel"
            >
              {{ cancelLabel }}
            </button>
            <button
              type="button"
              class="px-4 py-2 text-sm font-medium rounded-full transition-colors"
              :class="
                variant === 'danger'
                  ? 'bg-error text-on-error hover:bg-error/90'
                  : 'dashboard-primary-btn'
              "
              :aria-label="confirmLabel"
              @click="handleConfirm"
            >
              {{ confirmLabel }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, watch, onBeforeUnmount } from "vue";

const props = defineProps({
  open: { type: Boolean, default: false },
  title: { type: String, required: true },
  message: { type: String, required: true },
  confirmLabel: { type: String, default: "Confirm" },
  cancelLabel: { type: String, default: "Cancel" },
  variant: {
    type: String,
    default: "default",
    validator: (v) => ["default", "danger"].includes(v),
  },
});

const emit = defineEmits(["confirm", "cancel"]);

const titleId = "confirm-modal-title";
const messageId = "confirm-modal-message";

const dialogRef = ref(null);
const cancelBtnRef = ref(null);

const FOCUSABLE =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

const trapFocus = (e) => {
  if (e.key !== "Tab" || !dialogRef.value) return;
  const focusable = [
    ...dialogRef.value.querySelectorAll(FOCUSABLE),
  ].filter((el) => !el.disabled);
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
};

const handleConfirm = () => emit("confirm");
const handleCancel = () => emit("cancel");

const onKeydown = (e) => {
  if (e.key === "Escape" && props.open) handleCancel();
};

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      document.addEventListener("keydown", onKeydown);
      requestAnimationFrame(() => cancelBtnRef.value?.focus());
    } else {
      document.removeEventListener("keydown", onKeydown);
    }
  }
);

onBeforeUnmount(() => {
  document.removeEventListener("keydown", onKeydown);
});
</script>

<style scoped>
.confirm-modal-enter-active,
.confirm-modal-leave-active {
  transition: opacity 0.2s ease;
}

.confirm-modal-enter-from,
.confirm-modal-leave-to {
  opacity: 0;
}

.dashboard-primary-btn {
  padding: 0.5rem 1.25rem;
  border-radius: 9999px;
  background-color: #154212;
  color: #a9f862;
  font-weight: 600;
  border: none;
  box-shadow: 0 4px 10px rgba(21, 66, 18, 0.25);
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
}

.dashboard-primary-btn:hover {
  background-color: #2d5a27;
  box-shadow: 0 6px 16px rgba(21, 66, 18, 0.3);
}
</style>
